/**
 * Les durées du passage entre le poster de repli et la scène en volume.
 *
 * ⛔⛔ CES QUATRE NOMBRES PORTENT UNE GARANTIE, ET C'EST DE L'ARITHMÉTIQUE, PAS
 * UN RÉGLAGE. Deux calques superposés couvrent `dessus + (1 - dessus) × dessous`.
 * Tant que ce produit vaut 1, le cadre est plein ; en dessous, le fond
 * transparaît et l'objet a l'air de respirer au milieu de son apparition.
 *
 * 🟢 Trois mesures du 2026-09-09, sur écran puis recalculées, qui se recoupent :
 *
 *   | Réglage                                   | Couverture au plus creux |
 *   |-------------------------------------------|--------------------------|
 *   | fondu croisé symétrique                    | 75,0 %                   |
 *   | premier séquencement, retard trop court    | 98,1 %                   |
 *   | premier retour, canvas parti avant l'image | 65,7 %                   |
 *
 * ⛔ LE RETOUR ÉTAIT LE PIRE DES TROIS, et il était passé inaperçu parce qu'on
 * ne le voit qu'à la perte du contexte WebGL. Le canvas s'effaçait en 260 ms
 * pendant que le poster arrivait en 380 ms, tous deux partant de zéro : le
 * calque du dessus était parti avant que celui du dessous ne soit là. Pire que
 * le fondu croisé que ce fichier existe pour éviter.
 *
 * ⭐ LA GARANTIE EST STRUCTURELLE, PAS NUMÉRIQUE, et c'est ce qui la rend
 * vérifiable sans modéliser la courbe d'accélération du navigateur :
 *
 *   1. À L'ALLER, le poster tient sa PLEINE opacité tant que la scène n'a pas
 *      atteint la sienne. Le retard d'effacement est donc égal à la durée
 *      d'arrivée, jamais inférieur. Sur `[0, ARRIVEE]` le dessous vaut 1, sur
 *      `[ARRIVEE, ...]` le dessus vaut 1 : la couverture vaut 1 des deux côtés,
 *      quelle que soit l'accélération.
 *   2. AU RETOUR, le poster est REPOSÉ, pas fondu. Zéro milliseconde. Le
 *      dessous vaut donc 1 dès le premier instant, et la couverture vaut 1 quoi
 *      que fasse le dessus. ⭐ Et c'est aussi ce qui est physiquement juste :
 *      un contexte WebGL perdu, c'est une toile VIDE. Faire arriver son
 *      remplaçant en fondu, c'est fondre à partir de rien.
 *
 * ⚠️ Une garantie structurelle se contrôle par une inégalité entre deux
 * constantes, pas par un seuil toléré sur une mesure. Le contrôle vit dans
 * `banc/fondu.test.mjs`, et il lit CE fichier plutôt que de relire le composant.
 */

/** La scène monte à pleine opacité, par-dessus un poster qui ne bouge pas. */
export const ARRIVEE_MS = 560

/** Puis le poster s'efface, une fois la scène pleine. */
export const EFFACEMENT_MS = 320

/**
 * ⛔ Jamais inférieur à `ARRIVEE_MS`. C'est toute la garantie de l'aller, et
 * l'écrire comme une égalité plutôt que comme un nombre empêche la dérive : un
 * réglage de la durée d'arrivée emmène le retard avec lui.
 */
export const EFFACEMENT_DELAI_MS = ARRIVEE_MS

/**
 * Le retrait de la scène quand le contexte WebGL est perdu.
 *
 * ⚠️ Libre de toute contrainte : le poster étant déjà reposé dessous, la
 * couverture vaut 1 quelle que soit cette valeur. C'est du confort, pas une
 * sécurité. Plus court que l'arrivée parce qu'une apparition se savoure et
 * qu'une disparition se subit.
 */
export const RETRAIT_MS = 260

/** ⛔ Zéro, et ce zéro est la garantie du retour. Voir en tête de fichier. */
export const RETOUR_MS = 0

/**
 * La couverture du cadre par deux calques superposés, de 0 à 1.
 *
 * ⭐ Écrite ici plutôt que dans le contrôle : un contrôle qui réimplémente la
 * formule qu'il vérifie ne vérifie plus que lui-même.
 */
export function couverture(dessus: number, dessous: number): number {
  return dessus + (1 - dessus) * dessous
}
